/**
 * docs/UX.md §3 12–13 「发布任务给全班」 — 模板 → 一次确认.
 *
 * A 班级教师 never builds a batch by hand: they pick a class, pick one of six
 * ready-made templates, read one confirmation sentence and press the button.
 * Everything the 新建批次 wizard asks for (批次编号 / 阶段 / 任务类型 / 素材 /
 * 派发策略 / 成对) is derived here:
 *
 *   批次编号  `P0-<CLASSID>-<YYYYMMDD>[-n]`（n 只在同日重复发布时出现）
 *   阶段      P0
 *   任务/素材 模板决定
 *   派发      完整会话 → `session_plan`；单项模板 → `manual` + 该模板的素材
 *   成对      语料库模板永远 T5↔T6 成对；《AI星海正音》只在勾了「古诗词加演唱」时成对
 *
 * The three server calls (create → publish → dispatch) are run as one step with
 * explicit per-step bookkeeping, so a batch that published but failed to
 * dispatch offers 重试派发 instead of a dead end.
 */
import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AI_TEST_COLLECTION,
  AI_TEST_PART_LABEL,
  AI_TEST_PART_RANGE_LABEL,
  AI_TEST_TITLE,
  DEFAULT_COLLECTION,
  batchDispatchSchema,
} from "@xinghai/shared";
import type { BatchDispatchPayload, Task } from "@xinghai/shared";
import { Button, Icon, Select, Sheet, Stepper, useToast } from "@xinghai/ui";
import { batchesApi, materialsApi, teacherApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { ErrorNotice, LoadingBlock } from "./common";
import { TASK_LABEL } from "./labels";
import type {
  BatchListItem,
  DashboardClass,
  DashboardStudent,
  DispatchReport,
  MaterialSummary,
} from "./types";

// --------------------------------------------------------------------------- //
// 模板
// --------------------------------------------------------------------------- //

export type TemplateId =
  "session" | "t2" | "t3" | "t4" | "song" | "t7" | "ai1" | "ai2" | "ai3" | "ai_all";

export interface PublishTemplate {
  id: TemplateId;
  label: string;
  /** One line: what the student will be asked to do. */
  blurb: string;
  tasks: Task[];
  /** 完整会话 lets the server assemble the §2.2 package per speaker. */
  strategy: "session_plan" | "manual";
  recommended?: boolean;
  /**
   * 《AI星海正音》templates dispatch one authored collection instead of a task
   * quota: every material of `collection`, restricted to `levels`（教材的三个大
   * 部分）, in `sort_key` order. `tasks` is then derived from what the library
   * actually holds.
   */
  collection?: string;
  levels?: number[];
}

/** The group a template card belongs to on the 选模板 step. */
export interface TemplateGroup {
  id: string;
  label: string;
  hint: string;
  templates: readonly PublishTemplate[];
}

/** docs/UX.md §3 item 12 — the six cards, 完整会话 first and pre-selected. */
export const PUBLISH_TEMPLATES: readonly PublishTemplate[] = [
  {
    id: "session",
    label: "完整会话",
    blurb: "一整轮：单字、绕口令、段落、歌词朗读与演唱、听辨，一次发全。",
    tasks: [
      "T2_diagnostic",
      "T3_tongue_twister",
      "T4_passage",
      "T5_lyric_reading",
      "T6_singing",
      "T7_perception",
    ],
    strategy: "session_plan",
    recommended: true,
  },
  {
    id: "t2",
    label: "单字诊断",
    blurb: "全套单字，学生一个字一条，最快能看出问题在哪。",
    tasks: ["T2_diagnostic"],
    strategy: "manual",
  },
  {
    id: "t3",
    label: "绕口令",
    blurb: "按方言常见混淆挑的绕口令，每首慢速、正常各一遍。",
    tasks: ["T3_tongue_twister"],
    strategy: "manual",
  },
  {
    id: "t4",
    label: "段落朗读",
    blurb: "两段朗读，逐句录，声乐生优先「艺术朗读」。",
    tasks: ["T4_passage"],
    strategy: "manual",
  },
  {
    id: "song",
    label: "歌曲（朗读+演唱）",
    blurb: "两首歌的全部乐句，先念歌词再唱，成对绑定。",
    tasks: ["T5_lyric_reading", "T6_singing"],
    strategy: "manual",
  },
  {
    id: "t7",
    label: "听辨",
    blurb: "听两段判断异同，不用开麦，坐着就能做。",
    tasks: ["T7_perception"],
    strategy: "manual",
  },
];

/** 杨爽老师《AI星海正音》 — 三个大部分各一张卡，再加一张「全部 29 项」. */
export const AI_TEST_TEMPLATES: readonly PublishTemplate[] = [
  {
    id: "ai1",
    label: `星海正音 · ${AI_TEST_PART_RANGE_LABEL[1]}`,
    blurb: "音素呼读、气息、《数枣儿》《说时事》《花非花》《小兰儿》《正音歌》。",
    tasks: [],
    strategy: "manual",
    collection: AI_TEST_COLLECTION,
    levels: [1],
  },
  {
    id: "ai2",
    label: `星海正音 · ${AI_TEST_PART_RANGE_LABEL[2]}`,
    blurb: "双唇、唇齿、舌尖、舌根、舌面、平翘舌、前后鼻音，八组辅音绕口令。",
    tasks: [],
    strategy: "manual",
    collection: AI_TEST_COLLECTION,
    levels: [2],
  },
  {
    id: "ai3",
    label: `星海正音 · ${AI_TEST_PART_RANGE_LABEL[3]}`,
    blurb: "十三辙口诀，每一辙一首绕口令加一首古诗词。",
    tasks: [],
    strategy: "manual",
    collection: AI_TEST_COLLECTION,
    levels: [3],
  },
  {
    id: "ai_all",
    label: "星海正音 · 全部 29 项",
    blurb: "01 到 29 一次发全，学生按部分依次做。",
    tasks: [],
    strategy: "manual",
    collection: AI_TEST_COLLECTION,
    levels: [1, 2, 3],
  },
];

export const TEMPLATE_GROUPS: readonly TemplateGroup[] = [
  {
    id: "corpus",
    label: "语料采集",
    hint: "P0 语料库：按 §2.2 会话计划采集，用于标注与导出。",
    templates: PUBLISH_TEMPLATES,
  },
  {
    id: "ai_test",
    label: `杨爽老师 · ${AI_TEST_TITLE}`,
    hint: "绕口令与古诗词，01–29 项，分三个部分。每条只录一遍，按顺序做。",
    templates: AI_TEST_TEMPLATES,
  },
];

export const ALL_TEMPLATES: readonly PublishTemplate[] = TEMPLATE_GROUPS.flatMap(
  (g) => g.templates,
);

/** A2 §2.2 每位说话人的会话配额 — mirrors `services/dispatch.ts SESSION_PLAN`. */
export const SESSION_QUOTA = {
  tongue_twisters: 6,
  paragraphs: 2,
  songs: 2,
  perception_max: 40,
} as const;

/**
 * Rough seconds per recording, calibrated so the 完整会话 card lands on the
 * §2.2 「22 分钟」 session budget for the seeded corpus. Only ever used for the
 * 「约 N 分钟」 hint on the card — never sent to the server.
 */
const SECONDS_PER_ITEM: Record<Task, number> = {
  T1_reference: 10,
  T1b_reference_singing: 10,
  T2_diagnostic: 2,
  /** 慢速 + 正常 两遍. */
  T3_tongue_twister: 40,
  T4_passage: 45,
  T5_lyric_reading: 12,
  T6_singing: 15,
  T7_perception: 5,
};

/** `SONG-03-05` → `SONG-03`: the song a phrase pair belongs to (server parity). */
export function songKeyOf(m: Pick<MaterialSummary, "pair_group">): string | null {
  if (!m.pair_group) return null;
  const parts = m.pair_group.split("-");
  return parts.length > 2 ? parts.slice(0, -1).join("-") : m.pair_group;
}

const byId = (a: { id: string }, b: { id: string }) => a.id.localeCompare(b.id);

/**
 * The §2.2 quota applied to one task's materials — the same rule the server's
 * `sessionPlanFor` uses, so the number on the card is the number the student
 * actually gets whichever strategy ends up being sent.
 */
export function quotaFor(task: Task, materials: MaterialSummary[]): MaterialSummary[] {
  const rows = materials.filter((m) => m.task === task).sort(byId);
  switch (task) {
    case "T3_tongue_twister":
      return rows.slice(0, SESSION_QUOTA.tongue_twisters);
    case "T4_passage":
      return rows.slice(0, SESSION_QUOTA.paragraphs);
    case "T7_perception":
      return rows.slice(0, SESSION_QUOTA.perception_max);
    case "T5_lyric_reading":
    case "T6_singing": {
      const keys: string[] = [];
      for (const m of rows) {
        const k = songKeyOf(m) ?? m.id;
        if (!keys.includes(k)) keys.push(k);
      }
      const keep = new Set(keys.slice(0, SESSION_QUOTA.songs));
      return rows.filter((m) => keep.has(songKeyOf(m) ?? m.id));
    }
    default:
      return rows;
  }
}

export interface TemplatePlan {
  template: PublishTemplate;
  /** Task types the batch is created with — derived for collection templates. */
  tasks: Task[];
  /** Every material of the template's tasks — what the batch is created with. */
  materialIds: string[];
  /** The §2.2 quota — what one student is actually asked to record. */
  dispatchMaterialIds: string[];
  /** Per-task human summary, e.g. `240 个单字` / `6 首 × 2 遍`. */
  lines: string[];
  /** Recordings one student ends up with. */
  items: number;
  minutes: number;
  /** Distinct 歌曲 in the plan (T5/T6 templates only). */
  songs: number;
  /**
   * 著作权仍在保护期的曲目名。校订版（ai_test_v4）里一首都没有了，但机制保留。采集照常，
   * 卡片上只加一行说明，不拦发布。
   */
  inCopyright: string[];
}

/** `28 江阳辙 ang ·《在水一方》` → `《在水一方》`. */
export function pieceNameOf(m: Pick<MaterialSummary, "section">): string {
  const s = m.section ?? "";
  const at = s.lastIndexOf("·");
  return (at >= 0 ? s.slice(at + 1) : s).trim();
}

/** Distinct piece names among rows whose material is still in copyright. */
export function inCopyrightPieces(rows: MaterialSummary[]): string[] {
  const out: string[] = [];
  for (const m of rows) {
    if (m.rights !== "in_copyright") continue;
    const name = pieceNameOf(m) || m.title;
    if (!out.includes(name)) out.push(name);
  }
  return out;
}

function lineFor(task: Task, rows: MaterialSummary[], songs: number): string | null {
  if (rows.length === 0) return null;
  switch (task) {
    case "T2_diagnostic":
      return `${rows.length} 个单字`;
    case "T3_tongue_twister":
      return `${rows.length} 首绕口令 × 2 遍（慢速 + 正常）`;
    case "T4_passage":
      return `${rows.length} 段朗读`;
    case "T5_lyric_reading":
      return `${songs} 首歌的歌词朗读（${rows.length} 句）`;
    case "T6_singing":
      return `${songs} 首歌的演唱（${rows.length} 句）`;
    case "T7_perception":
      return `${rows.length} 道听辨题`;
    default:
      return `${rows.length} 条${TASK_LABEL[task]}`;
  }
}

/** `sort_key` then id — the authored order of a collection. */
export function bySortKey(a: MaterialSummary, b: MaterialSummary): number {
  return (a.sort_key ?? 0) - (b.sort_key ?? 0) || a.id.localeCompare(b.id);
}

/**
 * Every material of an 《AI星海正音》 template's parts, in authored order.
 * 演唱 (T6) rows are only included when the teacher ticked 「古诗词加演唱」.
 */
export function collectionRowsFor(
  template: PublishTemplate,
  materials: MaterialSummary[],
  withSinging = false,
): MaterialSummary[] {
  if (!template.collection) return [];
  const levels = template.levels ?? [];
  return materials
    .filter(
      (m) =>
        m.collection === template.collection &&
        (levels.length === 0 || (m.level !== null && levels.includes(m.level))) &&
        (withSinging || m.task !== "T6_singing"),
    )
    .sort((a, b) => (a.level ?? 0) - (b.level ?? 0) || bySortKey(a, b));
}

/** `17 发花辙 a ·《白石塔》` → one summary line per 条目（老师的 01–29 编号）. */
function collectionLines(rows: MaterialSummary[], withSinging: boolean): string[] {
  const items: string[] = [];
  for (const m of rows) {
    const s = (m.section ?? "").slice(0, 2);
    if (s && !items.includes(s)) items.push(s);
  }
  const reading = rows.filter((m) => m.task !== "T6_singing").length;
  const singing = rows.length - reading;
  const lines = [`${items.length} 项 · ${reading} 条朗读`];
  if (withSinging && singing > 0) lines.push(`另加 ${singing} 条古诗词演唱（读完再唱，成对）`);
  const over = rows.filter((m) => (m.max_duration_ms ?? 0) > 22_000);
  if (over.length > 0) lines.push(`其中 ${over.length} 条气息题可录到 40 秒`);
  lines.push("每条只录一遍");
  return lines;
}

/** What one student gets from `template`, given the whole material library. */
export function planFor(
  template: PublishTemplate,
  materials: MaterialSummary[],
  withSinging = false,
): TemplatePlan {
  if (template.collection) {
    const rows = collectionRowsFor(template, materials, withSinging);
    const ids = rows.map((m) => m.id);
    const seconds = rows.reduce(
      (sum, m) =>
        sum +
        Math.min(
          (m.max_duration_ms ?? 22_000) / 1000,
          // 唱一遍比念一遍慢
          (m.task === "T6_singing" ? 10 : 8) +
            m.char_count * (m.task === "T6_singing" ? 0.9 : 0.55),
        ),
      0,
    );
    return {
      template,
      tasks: [...new Set(rows.map((m) => m.task))],
      materialIds: ids,
      dispatchMaterialIds: ids,
      lines: rows.length > 0 ? collectionLines(rows, withSinging) : [],
      items: rows.length,
      minutes: Math.max(1, Math.round(seconds / 60)),
      songs: 0,
      inCopyright: inCopyrightPieces(rows),
    };
  }

  // A corpus template only ever draws from the P0 corpus —《星海正音小程序录音资料》
  // has its own cards and must never be swept into 完整会话.
  const corpus = materials.filter(
    (m) => (m.collection ?? DEFAULT_COLLECTION) === DEFAULT_COLLECTION,
  );
  const all: string[] = [];
  const dispatch: string[] = [];
  const lines: string[] = [];
  let items = 0;
  let seconds = 0;
  let songs = 0;

  for (const task of template.tasks) {
    const every = corpus.filter((m) => m.task === task).sort(byId);
    all.push(...every.map((m) => m.id));
    const rows = quotaFor(task, corpus);
    dispatch.push(...rows.map((m) => m.id));
    if (task === "T5_lyric_reading" || task === "T6_singing") {
      songs = Math.max(songs, new Set(rows.map((m) => songKeyOf(m) ?? m.id)).size);
    }
    const takes = task === "T3_tongue_twister" ? 2 : 1;
    items += rows.length * takes;
    seconds += rows.length * SECONDS_PER_ITEM[task];
    const line = lineFor(task, rows, songs);
    if (line) lines.push(line);
  }

  return {
    template,
    tasks: template.tasks,
    materialIds: all,
    dispatchMaterialIds: dispatch,
    lines,
    items,
    minutes: Math.max(1, Math.round(seconds / 60)),
    songs,
    inCopyright: [],
  };
}

// --------------------------------------------------------------------------- //
// 重复发布检测
// --------------------------------------------------------------------------- //

/** How much of a new plan has to be already out there to count as a repeat. */
export const DUPLICATE_OVERLAP = 0.8;

/** The name the warning uses — 「辅音正音」, not the full card label. */
export function shortTemplateName(t: PublishTemplate): string {
  if (t.collection === AI_TEST_COLLECTION && t.levels?.length === 1) {
    return AI_TEST_PART_LABEL[t.levels[0]!] ?? t.label;
  }
  return t.label;
}

/** `2026-09-17T…` → 「9 月 17 日」 */
export function publishDayLabel(iso: string | null | undefined): string {
  if (!iso) return "之前";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "之前";
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}

export interface DuplicatePublish {
  batch: BatchListItem;
  /** Share of the new plan's materials this batch already covers, 0–1. */
  overlap: number;
  recorded: number;
  required: number;
  dayLabel: string;
  /** What `retest_round` a re-publish should carry so it is not a second baseline. */
  nextRetestRound: number;
}

/**
 * The `UNIQUE (batch_id, speaker_id, material_id, retest_round)` constraint is
 * **per batch**, so publishing the same template to the same class twice quietly
 * gives every student the same items again under a new batch id. Before the
 * teacher confirms, look for a non-frozen batch that already covers ≥ 80 % of
 * this plan's materials for this class.
 */
export function findDuplicatePublish(
  classId: string,
  plan: TemplatePlan,
  batches: readonly BatchListItem[],
): DuplicatePublish | null {
  const wanted = new Set(plan.dispatchMaterialIds);
  if (wanted.size === 0) return null;

  const matches = batches
    .filter((b) => b.state !== "frozen" && (b.class_ids ?? []).includes(classId))
    .map((b) => {
      const hit = (b.material_ids ?? []).filter((id) => wanted.has(id)).length;
      return { batch: b, overlap: hit / wanted.size };
    })
    .filter((m) => m.overlap >= DUPLICATE_OVERLAP);
  if (matches.length === 0) return null;

  // newest first, so the warning names the most recent round
  const when = (b: BatchListItem) => b.published_at ?? b.created_at ?? "";
  matches.sort(
    (a, z) => when(z.batch).localeCompare(when(a.batch)) || z.batch.id.localeCompare(a.batch.id),
  );
  const top = matches[0]!;
  return {
    batch: top.batch,
    overlap: top.overlap,
    recorded: top.batch.recorded_count,
    required: top.batch.assignment_count,
    dayLabel: publishDayLabel(when(top.batch)),
    // one repeat → round 1, two → round 2 …
    nextRetestRound: matches.length,
  };
}

// --------------------------------------------------------------------------- //
// 批次编号
// --------------------------------------------------------------------------- //

const pad = (n: number) => String(n).padStart(2, "0");

/** `P0-<CLASSID>-<YYYYMMDD>`, with `-2`, `-3` … only when that id is taken. */
export function nextBatchId(
  classId: string,
  existingIds: readonly string[],
  now: Date = new Date(),
): string {
  const day = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const base = `P0-${classId.toUpperCase()}-${day}`;
  const taken = new Set(existingIds);
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

// --------------------------------------------------------------------------- //
// flow
// --------------------------------------------------------------------------- //

type Stage = "idle" | "running" | "done" | "failed";

interface RunState {
  stage: Stage;
  /** Set as soon as the batch exists, so 重试 never creates a second one. */
  batchId: string | null;
  created: boolean;
  published: boolean;
  report: DispatchReport | null;
  error: string | null;
  /** Which of the three calls blew up — decides the retry label. */
  failedAt: "create" | "publish" | "dispatch" | null;
}

const IDLE: RunState = {
  stage: "idle",
  batchId: null,
  created: false,
  published: false,
  report: null,
  error: null,
  failedAt: null,
};

export interface PublishToClassFlowProps {
  /** `sheet` for the 班级看板 button, `inline` for the teacher's /m/batches page. */
  mode?: "sheet" | "inline";
  /** Pre-selected class. Falls back to the first class the teacher owns. */
  classId?: string | null;
  onClose: () => void;
  /** Where 完成 goes. @default "/m" */
  doneHref?: string;
}

const STEPS = [
  { id: "class", label: "1 选班级" },
  { id: "template", label: "2 选模板" },
  { id: "confirm", label: "3 确认发布" },
];

export function PublishToClassFlow({
  mode = "sheet",
  classId: initialClassId = null,
  onClose,
  doneHref = "/m",
}: PublishToClassFlowProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [classId, setClassId] = useState<string>(initialClassId ?? "");
  const [templateId, setTemplateId] = useState<TemplateId>("session");
  /** 「古诗词加演唱（读完再唱，成对）」 — 默认不勾. */
  const [withSinging, setWithSinging] = useState(false);
  const [run, setRun] = useState<RunState>(IDLE);

  const dashboard = useQuery({
    queryKey: qk.teacherDashboard(),
    queryFn: ({ signal }) => teacherApi.dashboard({}, signal),
  });
  const materials = useQuery({
    queryKey: qk.materials({ all: true }),
    queryFn: ({ signal }) => materialsApi.list({}, signal),
  });
  const batches = useQuery({
    queryKey: qk.batches,
    queryFn: ({ signal }) => batchesApi.list(signal),
  });

  const { me } = useAuth();
  const classes: DashboardClass[] = useMemo(() => {
    const all = dashboard.data?.classes ?? [];
    const mine = new Set(me?.teaches_class_ids ?? []);
    if (mine.size === 0) return all;
    return [...all.filter((c) => mine.has(c.id)), ...all.filter((c) => !mine.has(c.id))];
  }, [dashboard.data, me?.teaches_class_ids]);
  const cls = useMemo(
    () => classes.find((c) => c.id === classId) ?? classes[0] ?? null,
    [classes, classId],
  );

  const students: DashboardStudent[] = useMemo(() => {
    const all = dashboard.data?.students ?? [];
    if (!cls) return [];
    if (classes.length <= 1) return all;
    return all.filter((s) => !s.class_id || s.class_id === cls.id);
  }, [dashboard.data, cls, classes.length]);

  const speakerIds = useMemo(() => students.map((s) => s.speaker_id), [students]);

  const template = ALL_TEMPLATES.find((t) => t.id === templateId) ?? PUBLISH_TEMPLATES[0]!;
  const materialList = useMemo(() => materials.data?.materials ?? [], [materials.data]);
  const plan = useMemo(
    () => planFor(template, materialList, withSinging),
    [template, materialList, withSinging],
  );

  // Has this class already had (≥80 % of) this plan? Re-publishing as a baseline
  // would make every student record the same items twice.
  const duplicate = useMemo(
    () => (cls ? findDuplicatePublish(cls.id, plan, batches.data?.batches ?? []) : null),
    [cls, plan, batches.data],
  );

  const batchId = useMemo(() => {
    if (run.batchId) return run.batchId;
    if (!cls) return "";
    return nextBatchId(
      cls.id,
      (batches.data?.batches ?? []).map((b) => b.id),
    );
  }, [run.batchId, cls, batches.data]);

  // ---- create → publish → dispatch ---------------------------------------- //

  const dispatchPayload = useCallback((): BatchDispatchPayload => {
    const draft = {
      speaker_ids: speakerIds,
      ...(template.strategy === "manual" ? { material_ids: plan.dispatchMaterialIds } : {}),
      strategy: template.strategy,
      // 《AI星海正音》: 只有勾了「古诗词加演唱」才把 T5↔T6 成对绑起来
      pair_t5_t6: template.collection ? withSinging : true,
      // A repeat is recorded as a 复测 round, not a second baseline — that keeps
      // the data honest and makes the server's DUPLICATE_BASELINE guard pass.
      ...(duplicate ? { retest_round: duplicate.nextRetestRound, allow_duplicate: true } : {}),
    };
    return batchDispatchSchema.parse(draft);
  }, [
    speakerIds,
    template.strategy,
    template.collection,
    withSinging,
    plan.dispatchMaterialIds,
    duplicate,
  ]);

  const runPublish = useCallback(
    async (from: RunState) => {
      if (!cls) return;
      const id = from.batchId ?? batchId;
      setRun({ ...from, stage: "running", batchId: id, error: null, failedAt: null });
      let state: RunState = { ...from, stage: "running", batchId: id, error: null, failedAt: null };

      try {
        if (!state.created) {
          await batchesApi.create({
            id,
            phase: "P0",
            tasks: plan.tasks,
            material_ids: plan.materialIds,
          });
          state = { ...state, created: true };
        }
        if (!state.published) {
          await batchesApi.publish(id);
          state = { ...state, published: true };
        }
        const res = await batchesApi.dispatch(id, dispatchPayload());
        void qc.invalidateQueries({ queryKey: qk.batches });
        void qc.invalidateQueries({ queryKey: ["teacher", "dashboard"] });
        toast.success(
          `已发布给 ${cls.name}：${res.dispatch.speakers} 位学生 · ${res.dispatch.assignments_created} 条任务`,
        );
        setRun({ ...state, stage: "done", report: res.dispatch });
      } catch (err) {
        const failedAt = !state.created ? "create" : !state.published ? "publish" : "dispatch";
        setRun({
          ...state,
          stage: "failed",
          failedAt,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [cls, batchId, plan.tasks, plan.materialIds, dispatchPayload, qc, toast],
  );

  const finish = () => {
    onClose();
    navigate(doneHref);
  };

  // ---- rendering ----------------------------------------------------------- //

  const loading = dashboard.isPending || materials.isPending || batches.isPending;
  const failedQuery = dashboard.isError ? dashboard : materials.isError ? materials : batches;

  let body: ReactNode;
  let footer: ReactNode;

  if (loading) {
    body = <LoadingBlock rows={3} label="正在准备发布任务" />;
    footer = (
      <Button variant="secondary" onClick={onClose}>
        取消
      </Button>
    );
  } else if (dashboard.isError || materials.isError || batches.isError) {
    body = (
      <ErrorNotice
        error={failedQuery.error}
        onRetry={() => void failedQuery.refetch()}
        title="发布任务需要的数据没能加载"
      />
    );
    footer = (
      <Button variant="secondary" onClick={onClose}>
        关闭
      </Button>
    );
  } else if (run.stage === "done") {
    body = <DonePanel cls={cls} report={run.report} batchId={run.batchId ?? batchId} />;
    footer = <Button onClick={finish}>完成</Button>;
  } else if (run.stage === "failed") {
    body = <FailurePanel run={run} batchId={run.batchId ?? batchId} />;
    footer = (
      <>
        <Button variant="secondary" onClick={onClose}>
          稍后再说
        </Button>
        <Button onClick={() => void runPublish(run)}>
          {run.failedAt === "dispatch" ? "重试派发" : "重试"}
        </Button>
      </>
    );
  } else if (!cls) {
    body = (
      <div className="xz-form">
        <p className="gz-muted">你名下还没有班级，先请课程团队给你分一个班。</p>
      </div>
    );
    footer = (
      <Button variant="secondary" onClick={onClose}>
        关闭
      </Button>
    );
  } else {
    const running = run.stage === "running";
    body = (
      <div className="xz-form">
        <Stepper steps={STEPS} current={step} label="发布任务步骤" />
        {step === 0 && (
          <ClassStep classes={classes} value={cls.id} onChange={setClassId} students={students} />
        )}
        {step === 1 && (
          <TemplateStep
            materials={materialList}
            value={templateId}
            onChange={setTemplateId}
            className={cls.name}
            withSinging={withSinging}
            onWithSingingChange={setWithSinging}
          />
        )}
        {step === 2 && (
          <ConfirmStep
            cls={cls}
            plan={plan}
            batchId={batchId}
            students={speakerIds.length}
            duplicate={duplicate}
          />
        )}
      </div>
    );
    // A repeat inverts the emphasis: 取消 becomes the primary action and
    // publishing again is offered quietly, as an explicit 复测.
    const repeat = step === 2 && duplicate !== null;
    footer = (
      <>
        <Button
          variant={repeat ? "primary" : "secondary"}
          onClick={step === 0 ? onClose : () => setStep(step - 1)}
        >
          {step === 0 ? "取消" : repeat ? "取消" : "上一步"}
        </Button>
        {step < 2 ? (
          <Button onClick={() => setStep(step + 1)}>下一步</Button>
        ) : (
          <Button
            variant={repeat ? "secondary" : "primary"}
            loading={running}
            disabled={speakerIds.length === 0}
            onClick={() => void runPublish(run)}
          >
            {repeat ? "仍然发布（作为复测）" : `发布给 ${cls.name} ${speakerIds.length} 人`}
          </Button>
        )}
      </>
    );
  }

  if (mode === "sheet") {
    return (
      <Sheet open onClose={onClose} title="发布任务给全班" size="lg" footer={footer}>
        {body}
      </Sheet>
    );
  }

  return (
    <section className="xz-publish" aria-label="发布任务给全班">
      {body}
      <div className="xz-publish__actions">{footer}</div>
    </section>
  );
}

// --------------------------------------------------------------------------- //
// steps
// --------------------------------------------------------------------------- //

function ClassStep({
  classes,
  value,
  onChange,
  students,
}: {
  classes: DashboardClass[];
  value: string;
  onChange: (id: string) => void;
  students: DashboardStudent[];
}) {
  const cls = classes.find((c) => c.id === value);
  return (
    <div className="xz-form">
      <label className="xz-picker__label" htmlFor="xz-publish-class">
        发布给哪个班
      </label>
      <Select
        id="xz-publish-class"
        aria-label="选择班级"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={classes.map((c) => ({ value: c.id, label: c.name }))}
      />
      <p className="gz-muted">
        {cls
          ? `${cls.name} 目前 ${students.length} 人已注册，授权没签全的会自动跳过。`
          : "选一个班级。"}
      </p>
    </div>
  );
}

function TemplateStep({
  materials,
  value,
  onChange,
  className,
  withSinging,
  onWithSingingChange,
}: {
  materials: MaterialSummary[];
  value: TemplateId;
  onChange: (id: TemplateId) => void;
  className: string;
  withSinging: boolean;
  onWithSingingChange: (on: boolean) => void;
}) {
  return (
    <fieldset className="xz-form__choices xz-tpl">
      <legend>{`给 ${className} 发什么`}</legend>
      {TEMPLATE_GROUPS.map((group) => (
        <div key={group.id} className="xz-tpl__group">
          <p className="xz-tpl__grouphead">
            <span className="xz-tpl__grouptitle">{group.label}</span>
            <span className="gz-caption">{group.hint}</span>
          </p>
          {group.templates.map((t) => {
            const plan = planFor(t, materials, withSinging);
            return (
              <label
                key={t.id}
                className="xz-form__choice xz-tpl__card"
                data-selected={value === t.id}
              >
                <input
                  type="radio"
                  name="publish-template"
                  value={t.id}
                  checked={value === t.id}
                  onChange={() => onChange(t.id)}
                />
                <span>
                  <span className="xz-tpl__title">
                    {t.label}
                    {t.recommended && <span className="xz-tpl__badge">推荐</span>}
                  </span>
                  <span className="gz-caption">{t.blurb}</span>
                  <span className="gz-caption xz-tpl__what">
                    {plan.lines.length > 0 ? plan.lines.join(" · ") : "素材库里还没有这类素材"}
                  </span>
                  <span className="gz-caption xz-tpl__eta">
                    每位学生约 {plan.minutes} 分钟 · {plan.items} 条
                  </span>
                  {plan.inCopyright.length > 0 && (
                    <span className="gz-caption xz-tpl__rights">
                      <Icon id="ui-info" size={16} /> 含 {plan.inCopyright.join("、")}
                      ，歌词仍在著作权保护期，商用前需取得授权（不影响采集）
                    </span>
                  )}
                </span>
              </label>
            );
          })}
          {group.id === "ai_test" && (
            <label className="xz-form__choice xz-tpl__option">
              <input
                type="checkbox"
                checked={withSinging}
                onChange={(e) => onWithSingingChange(e.target.checked)}
              />
              <span>
                <span>古诗词加演唱（读完再唱，成对）</span>
                <span className="gz-caption">
                  不勾只发朗读。勾上之后每一句古诗词都是先念一遍、再唱一遍，两条绑在一起。
                </span>
              </span>
            </label>
          )}
        </div>
      ))}
    </fieldset>
  );
}

function ConfirmStep({
  cls,
  plan,
  batchId,
  students,
  duplicate = null,
}: {
  cls: DashboardClass;
  plan: TemplatePlan;
  batchId: string;
  students: number;
  duplicate?: DuplicatePublish | null;
}) {
  return (
    <div className="xz-form">
      <p className="xz-publish__headline">
        发布给 <strong>{cls.name}</strong> <strong>{students}</strong> 人
      </p>
      {duplicate && (
        <div className="gz-notice gz-notice--error" role="alert">
          <Icon id="ui-warning" size={20} />
          <span>
            {`${cls.name} 在 ${duplicate.dayLabel}已经发布过「${shortTemplateName(plan.template)}」`}
            {`（已录 ${duplicate.recorded}/${duplicate.required}）。`}
            再发一轮，学生会重复录同样的内容。
            {` 如果这是有意的复测，会记成第 ${duplicate.nextRetestRound} 轮，不会覆盖上一轮的数据。`}
          </span>
        </div>
      )}
      <ul className="xz-publish__summary">
        <li>
          <Icon id="ui-tag" size={20} />
          <span>{plan.template.label}</span>
        </li>
        {plan.lines.map((line) => (
          <li key={line}>
            <Icon id="ui-check" size={20} />
            <span>{line}</span>
          </li>
        ))}
        <li>
          <Icon id="ui-clock" size={20} />
          <span>每位学生约 {plan.minutes} 分钟</span>
        </li>
      </ul>
      <div className="gz-notice" role="status">
        <Icon id="ui-info" size={20} />
        <span>
          确认后学生手机上马上就会出现新任务。授权没签全的学生会自动跳过。批次编号{" "}
          <span className="xz-adm__mono">{batchId}</span>，你不用记它。
        </span>
      </div>
      {students === 0 && (
        <div className="gz-notice gz-notice--error" role="alert">
          <Icon id="ui-warning" size={20} />
          <span>这个班还没有学生注册，先把邀请码发到课程群里。</span>
        </div>
      )}
    </div>
  );
}

function DonePanel({
  cls,
  report,
  batchId,
}: {
  cls: DashboardClass | null;
  report: DispatchReport | null;
  batchId: string;
}) {
  const skipped = report?.skipped_no_consent.length ?? 0;
  return (
    <div className="xz-form">
      <p className="xz-publish__headline">
        <Icon id="ui-check-circle" size={24} /> 已发布给 {cls?.name ?? "本班"}
      </p>
      <ul className="xz-publish__summary">
        <li>
          <Icon id="ui-users" size={20} />
          <span>{report?.speakers ?? 0} 位学生</span>
        </li>
        <li>
          <Icon id="ui-list" size={20} />
          <span>{report?.assignments_created ?? 0} 条任务</span>
        </li>
        {skipped > 0 && (
          <li>
            <Icon id="ui-warning" size={20} />
            <span>{skipped} 位因授权未签全被跳过</span>
          </li>
        )}
      </ul>
      <p className="gz-caption">
        批次编号 <span className="xz-adm__mono">{batchId}</span>。
      </p>
    </div>
  );
}

function FailurePanel({ run, batchId }: { run: RunState; batchId: string }) {
  const where =
    run.failedAt === "create"
      ? "批次没能建起来，还没有任何学生收到任务。"
      : run.failedAt === "publish"
        ? `批次 ${batchId} 已建好，但没能发布；学生还看不到任务。`
        : `批次 ${batchId} 已发布，但任务没能派发给学生；再试一次就好，不会重复派发已有的。`;
  return (
    <div className="xz-form">
      <div className="gz-notice gz-notice--error" role="alert">
        <Icon id="ui-error" size={20} />
        <span>
          {where}
          {run.error ? `（${run.error}）` : ""}
        </span>
      </div>
    </div>
  );
}
